import React, { useCallback, useEffect, useState } from 'react';
import {
    AppBar,
    Avatar,
    Badge,
    BottomNavigation,
    BottomNavigationAction,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Popover,
    Stack,
    SwipeableDrawer,
    Toolbar,
    Typography
} from "@mui/material";
import {
    AccessTime,
    AccountBalanceWallet,
    Close,
    CorporateFare,
    MenuOpen,
    PersonOutline,
    StarsOutlined
} from "@mui/icons-material";
import { toPriceWithComma, replacePersianNumbers, getUserAvatarSrc } from "../helper/utils";
import { connect, useDispatch, useSelector } from "react-redux";
import { sagaActions } from "../helper/redux/actions/SagaActions";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { bottomMenuItems } from "../helper/bottomMenuItems";
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DrawerLayout from "./DrawerLayout";
import TomanSvg from "../pages/basket/partial/_TomanSvg";
import { User_getMyCredits } from "../network/api/user.api";
import { invoice_getBasketByUserId } from "../network/api/invoice.api";
import { invoiceActions } from "../helper/redux/actions/InvoiceActions";
import ChatWidget from "../helper/layouts/ChatWidget";

// کامپوننت نمایش تایمر معکوس انقضای اعتبار
const CreditCountdown = ({ expireDate }) => {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!expireDate) return;
        const timer = setInterval(() => {
            setNow(Date.now());
        }, 1000);
        return () => clearInterval(timer);
    }, [expireDate]);

    if (!expireDate) {
        return (
            <Typography
                component="span"
                variant="caption"
                sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: "success.main",
                    mt: 0.3
                }}
            >
                دائمی
            </Typography>
        );
    }

    const expireTime = new Date(expireDate).getTime();
    if (isNaN(expireTime)) {
        return (
            <Typography
                component="span"
                variant="caption"
                sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: "text.secondary",
                    mt: 0.3
                }}
            >
                نامشخص
            </Typography>
        );
    }

    const diffMs = expireTime - now;

    if (diffMs <= 0) {
        return (
            <Typography
                component="span"
                variant="caption"
                sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: "error.main",
                    mt: 0.3
                }}
            >
                منقضی شده
            </Typography>
        );
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts = [];
    if (days > 0) parts.push(`${days} روز`);
    if (hours > 0 || days > 0) parts.push(`${hours} ساعت`);
    if (minutes > 0 || (days === 0 && hours === 0 && seconds === 0)) parts.push(`${minutes} دقیقه`);
    if (days === 0 && hours === 0 && seconds > 0) parts.push(`${seconds} ثانیه`);

    const countdownText = replacePersianNumbers(parts.join(" "));
    const solarDateStr = new Date(expireDate).toLocaleDateString('fa-IR');
    const isUrgent = days < 3;

    return (
        <Box
            component="span"
            title={`تاریخ دقیق انقضا: ${solarDateStr}`}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.3,
                mt: 0.3,
                cursor: "help"
            }}
        >
            {isUrgent && (
                <AccessTime sx={{ fontSize: "0.75rem", color: "warning.main" }} />
            )}
            <Typography
                component="span"
                variant="caption"
                sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: isUrgent ? "warning.main" : "text.secondary",
                    lineHeight: 1
                }}
            >
                تا {countdownText}
            </Typography>
        </Box>
    );
};

const PageLayout = ({
    RequestUser,
    RequestServerSettings,
    children
}) => {
    const navigate = useNavigate();
    const currentUser = useSelector(state => state.auth.user);
    const userBasket = useSelector(state => state.invoice.userBasket);
    const basketCount = userBasket?.InvoiceSubscribe?.length || 0;
    const location = useLocation();
    const dispatch = useDispatch();
    const [openDrawer, SetOpenDrawer] = useState(false);
    const setMenuOpen = useCallback((open) => SetOpenDrawer(open), []);

    const getTabName = useCallback((path) => {
        const pathParam = path.split('/')[1];
        if (["wallet", "basket", "invoices"].includes(pathParam)) {
            return null;
        }
        let selectedItem = bottomMenuItems[0].value;
        bottomMenuItems.forEach(function (item) {
            if (item.otherRouts.includes(pathParam)) {
                selectedItem = item.value;
            }
        });
        return selectedItem;
    }, []);

    const [value, setValue] = useState(() => getTabName(window.location.pathname));

    useEffect(() => {
        setValue(getTabName(location.pathname));
    }, [location.pathname, getTabName]);

    const currentUserId = currentUser?.Id;
    useEffect(() => {
        if (!currentUserId) return;

        const now = Date.now();
        const TWO_MINUTES_MS = 2 * 60 * 1000;
        const lastShellSync = Number(sessionStorage.getItem("gympin_last_shell_sync") || 0);
        const shouldSyncShell = !lastShellSync || now - lastShellSync >= TWO_MINUTES_MS;
        // userBasket is not persisted; after a full reload Redux is empty while sessionStorage may still throttle.
        const needsBasketHydration = userBasket === undefined;

        if (!shouldSyncShell && !needsBasketHydration) return;

        if (shouldSyncShell) {
            RequestUser();
        }

        invoice_getBasketByUserId(currentUserId)
            .then((result) => {
                dispatch(invoiceActions.SetUserBasket(result.data?.Data || null));
                if (shouldSyncShell) {
                    sessionStorage.setItem("gympin_last_shell_sync", String(Date.now()));
                }
            })
            .catch(() => {});
    }, [currentUserId, RequestUser, dispatch, userBasket]);

    const [walletAnchorEl, setWalletAnchorEl] = useState(null);
    const [userCreditsData, setUserCreditsData] = useState(null);
    const [loadingCredits, setLoadingCredits] = useState(false);

    // به‌روزرسانی موجودی و اعتبارات با کنترل بازه ۵ دقیقه‌ای
    const checkAndRefreshCredits = useCallback((force = false) => {
        if (!currentUserId) return;

        const now = Date.now();
        const TWO_MINUTES_MS = 2 * 60 * 1000;
        const lastFetch = Number(sessionStorage.getItem("gympin_last_credits_fetch") || 0);
        const moneyPage = location.pathname.startsWith("/wallet") || location.pathname.startsWith("/basket");

        if (force || moneyPage || !lastFetch || now - lastFetch >= TWO_MINUTES_MS) {
            setLoadingCredits(true);
            User_getMyCredits()
                .then((res) => {
                    if (res?.data?.Data) {
                        setUserCreditsData(res.data.Data);
                        sessionStorage.setItem("gympin_last_credits_fetch", Date.now().toString());
                        if (RequestUser) {
                            RequestUser();
                        }
                    }
                })
                .catch(() => { })
                .finally(() => {
                    setLoadingCredits(false);
                });
        }
    }, [currentUserId, RequestUser, location.pathname]);

    useEffect(() => {
        checkAndRefreshCredits();
    }, [location.pathname, checkAndRefreshCredits]);

    const displayBalance = userCreditsData?.TotalCredit !== undefined
        ? userCreditsData.TotalCredit
        : (currentUser?.Balance || 0);

    const usableCredits = (userCreditsData?.CreditDetails && userCreditsData.CreditDetails.length > 0)
        ? userCreditsData.CreditDetails.filter((c) => (c.CreditPayableAmount || c.CreditAmount || 0) > 0)
        : (currentUser?.Balance > 0 ? [{
            CreditType: "PERSONAL",
            CreditAmount: currentUser.Balance,
            CreditPayableAmount: currentUser.Balance,
            ExpireDate: null
        }] : []);

    return (
        <>

            <Box sx={{ flexGrow: 1 }}>
                <AppBar position={"fixed"} color={"primary"} >
                    <Toolbar>
                        <IconButton
                            size="large"
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            onClick={() => { SetOpenDrawer(!openDrawer) }}

                        >
                            <Badge badgeContent={basketCount} sx={{
                                '& .MuiBadge-standard': {
                                    // boxShadow: '2px 3px 4px #000000'
                                },
                            }} color={"manuBadge"} variant={"standard"}>
                                {openDrawer ? <MenuOpen sx={{ fontSize: "1.8rem" }} /> : <MenuIcon sx={{ fontSize: "1.8rem" }} />}
                            </Badge>
                        </IconButton>

                        <Typography variant="h6" component="div"
                            sx={{ flexGrow: 1 }}>
                        </Typography>
                        {currentUser && (
                            <Button
                                onClick={(e) => {
                                    setWalletAnchorEl(e.currentTarget);
                                    if (!userCreditsData) {
                                        checkAndRefreshCredits(true);
                                    }
                                }}
                                color="inherit"
                                sx={{
                                    borderRadius: 3,
                                    px: { xs: 1.2, sm: 1.8 },
                                    py: 0.6,
                                    mr: 1,
                                    bgcolor: "rgba(255, 255, 255, 0.15)",
                                    backdropFilter: "blur(8px)",
                                    border: "1px solid rgba(255, 255, 255, 0.25)",
                                    color: "#ffffff",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.8,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        bgcolor: "rgba(255, 255, 255, 0.25)",
                                        borderColor: "rgba(255, 255, 255, 0.4)",
                                        transform: "translateY(-1px)"
                                    }
                                }}
                            >
                                <AccountBalanceWallet sx={{ fontSize: { xs: 18, sm: 20 }, opacity: 0.9 }} />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 800,
                                            fontSize: { xs: "0.85rem", sm: "0.95rem" },
                                            lineHeight: 1
                                        }}
                                    >
                                        {toPriceWithComma(displayBalance)}
                                    </Typography>
                                    <TomanSvg size={14} color="white" />
                                </Box>
                            </Button>
                        )}

                        {/* پاپ‌اور باکس نمایش تفکیک اعتبارات و سررسید */}
                        <Popover
                            open={Boolean(walletAnchorEl)}
                            anchorEl={walletAnchorEl}
                            onClose={() => setWalletAnchorEl(null)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left'
                            }}
                            PaperProps={{
                                dir: "rtl",
                                style: { direction: "rtl", textAlign: "right" },
                                sx: {
                                    mt: 1.2,
                                    width: { xs: 320, sm: 360 },
                                    maxWidth: "94vw",
                                    borderRadius: 3.5,
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    boxShadow: (theme) =>
                                        theme.palette.mode === 'dark'
                                            ? "0 10px 30px rgba(0,0,0,0.6)"
                                            : "0 8px 26px rgba(0,0,0,0.1)"
                                }
                            }}
                        >
                            <Box dir="rtl" style={{ direction: "rtl", textAlign: "right" }} sx={{ width: "100%" }}>
                                <Stack spacing={1.8}>
                                    {/* سربرگ پاپ‌اور: عنوان در راست، دکمه بستن در چپ */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            width: "100%"
                                        }}
                                    >
                                        {/* سمت راست */}
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <AccountBalanceWallet color="primary" sx={{ fontSize: 20 }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                                موجودی و اعتبارات
                                            </Typography>
                                        </Box>

                                        {/* سمت چپ */}
                                        <IconButton size="small" onClick={() => setWalletAnchorEl(null)} sx={{ p: 0.4 }}>
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    {/* موجودی کل: متن در راست، مبلغ در چپ */}
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2.5,
                                            bgcolor: (theme) =>
                                                theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)',
                                            border: "1px solid",
                                            borderColor: (theme) =>
                                                theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.25)' : 'rgba(16, 185, 129, 0.2)',
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            width: "100%"
                                        }}
                                    >
                                        {/* سمت راست */}
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary" }}>
                                            موجودی قابل استفاده
                                        </Typography>

                                        {/* سمت چپ */}
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 900, color: "success.main" }}>
                                                {toPriceWithComma(displayBalance)}
                                            </Typography>
                                            <TomanSvg size={16} />
                                        </Box>
                                    </Box>

                                    <Divider />

                                    {/* لیست اعتبارات زیر هم */}
                                    <Box style={{ textAlign: "right", width: "100%" }}>
                                        {loadingCredits && !userCreditsData ? (
                                            <Box sx={{ py: 2, textAlign: "center" }}>
                                                <CircularProgress size={24} color="primary" />
                                            </Box>
                                        ) : usableCredits.length === 0 ? (
                                            <Typography variant="caption" sx={{ color: "text.disabled", display: "block", py: 1 }}>
                                                هیچ اعتبار فعالی یافت نشد.
                                            </Typography>
                                        ) : (
                                            <Stack spacing={1}>
                                                {usableCredits.map((credit, idx) => {
                                                    const isPersonal = credit.CreditType === "PERSONAL";
                                                    const isSponsor = credit.CreditType === "SPONSOR";
                                                    const isNonW = credit.CreditType === "NON_WITHDRAWABLE";

                                                    const title = isPersonal
                                                        ? "کیف پول شخصی"
                                                        : isSponsor
                                                            ? (credit.Corporate?.Name ? `اعتبار ${credit.Corporate.Name}` : "اعتبار سازمانی")
                                                            : isNonW
                                                                ? "اعتبار جیم‌پین"
                                                                : "اعتبار";

                                                    const amount = credit.CreditPayableAmount || credit.CreditAmount || 0;

                                                    return (
                                                        <Box
                                                            key={credit.Id || idx}
                                                            sx={{
                                                                p: 1.2,
                                                                borderRadius: 2,
                                                                bgcolor: "action.hover",
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                width: "100%"
                                                            }}
                                                        >
                                                            {/* سمت راست: آیکون و عنوان */}
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                {isPersonal ? (
                                                                    <PersonOutline sx={{ fontSize: 18, color: "primary.main" }} />
                                                                ) : isSponsor ? (
                                                                    <CorporateFare sx={{ fontSize: 18, color: "success.main" }} />
                                                                ) : (
                                                                    <StarsOutlined sx={{ fontSize: 18, color: "info.main" }} />
                                                                )}
                                                                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
                                                                    {title}
                                                                </Typography>
                                                            </Box>

                                                            {/* سمت چپ (روبروی آن): مبلغ و تاریخ انقضا به صورت تایمر معکوس */}
                                                            <Box
                                                                style={{ direction: "rtl", textAlign: "left" }}
                                                                sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}
                                                            >
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: "0.85rem", lineHeight: 1 }}>
                                                                        {toPriceWithComma(amount)}
                                                                    </Typography>
                                                                    <TomanSvg size={12} />
                                                                </Box>
                                                                <CreditCountdown expireDate={credit.ExpireDate} />
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        )}
                                    </Box>

                                    <Divider />

                                    {/* دکمه انتقال به صفحه کیف پول */}
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => {
                                            setWalletAnchorEl(null);
                                            navigate("/wallet");
                                        }}
                                        sx={{
                                            py: 1.2,
                                            borderRadius: 2.8,
                                            fontWeight: 800,
                                            fontSize: "0.9rem",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 1
                                        }}
                                    >
                                        <AccountBalanceWallet sx={{ fontSize: 19 }} />
                                        <span>کیف پول</span>
                                    </Button>
                                </Stack>
                            </Box>
                        </Popover>
                        {currentUser && (
                            <IconButton
                                color="inherit"
                                component={Link}
                                to="/profile/edit"
                                sx={{ p: 0.5, mr: 0.5 }}
                                title={currentUser?.FullName || "پروفایل کاربری"}
                            >
                                <Avatar
                                    alt={currentUser?.FullName || "کاربر"}
                                    src={getUserAvatarSrc(currentUser, 96)}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        border: "2px solid rgba(255, 255, 255, 0.9)",
                                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                                        bgcolor: "rgba(255, 255, 255, 0.2)",
                                        color: "#ffffff",
                                        fontWeight: 700,
                                        fontSize: "1rem"
                                    }}
                                >
                                    {currentUser?.FullName ? (
                                        currentUser.FullName.trim().charAt(0)
                                    ) : (
                                        <AccountCircleIcon sx={{ fontSize: "2.1rem" }} />
                                    )}
                                </Avatar>
                            </IconButton>
                        )}
                    </Toolbar>
                </AppBar>
            </Box>

            <SwipeableDrawer
                anchor={'left'}
                sx={{ zIndex: 1200 }}
                open={openDrawer}
                onClose={() => SetOpenDrawer(false)}
                onOpen={() => SetOpenDrawer(true)}
                ModalProps={{ keepMounted: false }}
            >
                {openDrawer && <DrawerLayout setMenuOpen={setMenuOpen} />}
            </SwipeableDrawer>
            <Paper sx={{ pt: { xs: 7, sm: 9 }, pb: 8, minHeight: "100vh", bgcolor: "background.default" }} elevation={0}>
                {children}
            </Paper>

            <ChatWidget />

            <Paper sx={{ position: 'fixed', mx: 2, bottom: 0, left: 0, right: 0, zIndex: 1098, borderRadius: "32px 32px 0 0", boxShadow: "0px 0px 15px rgba(0,0,0,0.3)", bgcolor: "background.paper" }} elevation={24}>
                <BottomNavigation
                    showLabels
                    value={value}
                    sx={{ borderRadius: "32px 32px 0 0", bgcolor: "background.paper" }}
                    onChange={(event, newValue) => {
                        setValue(newValue)
                    }}
                >
                    {bottomMenuItems.map((item, index) => (
                        <BottomNavigationAction key={index} sx={{
                            minWidth: "auto", ":after": {
                                content: '" "', bgcolor: (item.value === value) ? "#e7333e" : "transparent",
                                position: "absolute",
                                bottom: 0,
                                width: "90px",
                                justifySelf: "center",
                                height: "7px",
                                transition: "300ms all ease",
                                borderRadius: "7px 7px 0 0"
                            }
                        }} component={Link} to={item.firstDirection}
                            value={item.value} label={item.lable} icon={item.icon} />
                    ))}
                </BottomNavigation>
            </Paper>

        </>
    );
};

export default connect(null, sagaActions)(PageLayout);
